import React, { useState, useRef } from 'react'
import SearchDropdown from '@SiteComponents/SearchDropdown'
import GuidelinesDropdown from './GuidelinesDropdown'
import AddScriptButton from './AddScriptButton'
import Remove from '@Images/project_setup_form_remove_language.svg'
import Alert from '@Images/project_setup_form_alert.svg'
import ScriptFile from '@Images/project_setup_form_script_file.svg'

import {
  IGuideline,
} from '@SiteModules/Guideline/types'

import './ProjectVersionLanguage.scss'

interface ProjectVersionLanguageProps {
  readonly captionGuidelines: IGuideline[]
  readonly newProjectVersionLanguage: string
  readonly availableProjectVersionLanguages: string[]
  readonly captionGuidelineOption: string | null
  readonly showAlert: boolean
  readonly transcriptText: string
  readonly hasSrt: boolean
  readonly cannotImportScript: boolean
  readonly transcriptFileName: string | null
  setNewProjectVersionLanguage(newProjectVersionLanguage: string): void
  setCaptionGuidelineOption(newCaptionGuidelineOption: string | null): void
  setTranscriptText(newTranscriptText: string): void
  setHasSrt(newHasSrt: boolean): void
  setTranscriptFileName(newTranscriptFileName: string | null): void
}

const ProjectVersionLanguage: React.FC<ProjectVersionLanguageProps> = props => {
  const {
    captionGuidelines,
    availableProjectVersionLanguages,
    newProjectVersionLanguage,
    captionGuidelineOption,
    showAlert,
    transcriptText,
    hasSrt,
    cannotImportScript,
    transcriptFileName,
  } = props

  const [showButtons, setShowButtons] = useState(true)

  const fileRef = useRef<HTMLInputElement>(null)
  const willUseScript = transcriptText.length > 0

  const handleRemoveProjectVersionLanguage = () => {
    props.setNewProjectVersionLanguage('')
    props.setCaptionGuidelineOption(null)
    resetScriptUpload()
  }

  const setTranscriptTextAndFileName = (newScriptText: string, newFileName: string | null) => {
    props.setTranscriptText(newScriptText)
    props.setTranscriptFileName(newFileName)
  }

  const openFileManager = () => {
    if (fileRef.current) {
      fileRef.current.value = ''
    }

    fileRef.current?.click()
  }

  const resetScriptUpload = () => {
    setTranscriptTextAndFileName('', null)
    props.setHasSrt(false)
    if (fileRef.current) {
      fileRef.current.value = ''
    }
  }

  const renderButtons = () => {
    return (
      <div className="SetupProjectPopup__buttons">
        {renderAddScriptButton()}
        {renderGuidelineButton()}
        {renderRemoveButton()}
      </div>
    )
  }

  const renderAddScriptButton = () => {
    return (
      <AddScriptButton
        cannotImportScript={cannotImportScript}
        fileRef={fileRef}
        resetScriptUpload={resetScriptUpload}
        setScriptTextAndFileName={setTranscriptTextAndFileName}
        setHasSrt={props.setHasSrt}
        openFileManager={openFileManager}
      />
    )
  }

  const renderGuidelineButton = () => {
    return (
      <GuidelinesDropdown
        guidelines={captionGuidelines}
        guidelineOption={captionGuidelineOption}
        setGuidelineOption={props.setCaptionGuidelineOption}
      />
    )
  }

  const renderRemoveButton = () => {
    return (
      <div
        className="SetupProjectPopup__button"
        onClick={handleRemoveProjectVersionLanguage}
      >
        <Remove />
      </div>
    )
  }

  const renderScript = () => {
    return (
      <div className="SetupProjectPopup__script-wrapper">
        <div
          className="SetupProjectPopup__script"
          onClick={openFileManager}
        >
          <ScriptFile />
          <div className="SetupProjectPopup__script-name">
            {transcriptFileName}
          </div>
          {hasSrt && (
            <div className="SetupProjectPopup__script-srt">
              SRT File
            </div>
          )}
        </div>
        <div
          className="SetupProjectPopup__button"
          onClick={resetScriptUpload}
        >
          <Remove />
        </div>
      </div>
    )
  }

  const renderAlert = () => {
    return (
      <div className="SetupProjectPopup__alert">
        <Alert />
        You need to select the source language of your project.
      </div>
    )
  }

  return (
    <div className="ProjectVersionLanguage">
      <div className="SetupProjectPopup__section-header">
        Original language of your project
      </div>
      <div
        className="ProjectVersionLanguage__main ProjectVersionLanguage__element"
        id="project_version_language_main"
      >
        <SearchDropdown
          availableOptions={availableProjectVersionLanguages}
          selectedOption={newProjectVersionLanguage}
          setSelectedOption={props.setNewProjectVersionLanguage}
          showAlert={showAlert}
          setShowButtons={setShowButtons}
          classNameForClickOutsideListener="ProjectVersionLanguage__element"
          idNameOfParentContainer="project_version_language_main"
          placeholderText="Select the source language..."
          noOptionFoundText="No language found"
        />
        {showButtons && renderButtons()}
      </div>
      {willUseScript && renderScript()}
      {showAlert && renderAlert()}
    </div>
  )
}

export default ProjectVersionLanguage
